/**
 * overresponsibilityDetector.js
 *
 * Detects over-responsibility — absorbing total spiritual accountability
 * for others' outcomes; the interior disposition of feeling like
 * the singular responsible agent for the congregation's spiritual health.
 *
 * DISTINCT FROM pastoralLoadDistribution (FASE 10):
 *   pastoralLoadDistribution → "nadie más ayuda" (structural work distribution)
 *   THIS → the interior pattern: guilt for limits, feeling total spiritual
 *           responsibility, absorbing others' failures as personal failures.
 *
 * NOT called "messianic complex" in instructions or debug output.
 * Called: over-responsibility, centralización espiritual, presión salvadora.
 *
 * States:
 *   distribuido           — responsibility well-distributed; healthy delegation
 *   centrado_saludable    — engaged, responsible, but without pathological weight
 *   tendencia_sobrecarga  — beginning to over-absorb; watch for escalation
 *   sobreresponsabilidad  — carrying others' spiritual outcomes as personal burden
 *   centralización_crítica — everything depends spiritually on this one person
 */

const OVERRESPONSIBILITY_SIGNALS = [
  // Guilt for others' spiritual state (weight: 2)
  {
    weight: 2,
    patterns: [
      /(?:siento\s+que\s+(?:es\s+mi\s+culpa|fallé|fracasé)\s+(?:cuando|porque|si)\s+(?:alguien\s+(?:se\s+va|pierde\s+la\s+fe|abandona)|la\s+congregación\s+(?:no\s+crece|decae)))/i,
      /(?:me\s+responsabilizo\s+(?:de\s+(?:la\s+salvación|el\s+crecimiento\s+espiritual|la\s+fe)\s+de\s+(?:todos|la\s+gente|los\s+miembros)|por\s+lo\s+que\s+le\s+pasa\s+espiritualmente\s+a\s+(?:ellos|la\s+iglesia)))/i,
    ],
  },
  // "Everything depends on me" (weight: 2)
  {
    weight: 2,
    patterns: [
      /(?:si\s+yo\s+no\s+(?:oro|pido|intercedo|cuido|pastoreo|vigilo))\s+(?:por\s+ellos|nadie\s+lo\s+hará|nadie\s+más\s+va\s+a\s+hacerlo)/i,
      /(?:el\s+(?:estado\s+espiritual|bienestar|crecimiento|futuro)\s+(?:de\s+(?:la\s+iglesia|la\s+congregación|la\s+familia))\s+(?:depende\s+de\s+mí|es\s+mi\s+responsabilidad))/i,
    ],
  },
  // Can't delegate spiritually (weight: 2)
  {
    weight: 2,
    patterns: [
      /(?:no\s+(?:puedo|me\s+atrevo|me\s+permito)\s+delegar\s+(?:algo\s+(?:tan\s+)?(?:importante|espiritual|serio)|la\s+pastoral|el\s+cuidado))/i,
      /(?:si\s+(?:delego|dejo\s+que\s+otro)\s+(?:lo\s+haga|se\s+encargue))\s+(?:siento\s+que|es\s+como\s+si?)\s+(?:abandono|fallo|no\s+cumplo)/i,
    ],
  },
  // Absorbing all congregational problems as personal (weight: 1)
  {
    weight: 1,
    patterns: [
      /(?:cuando\s+(?:alguien|una\s+familia|un\s+miembro)\s+(?:tiene\s+(?:un\s+problema|una\s+crisis|una\s+caída)|pasa\s+algo)\s+(?:lo\s+(?:tomo|siento|vivo)\s+como\s+(?:mío|personal|mi\s+fracaso)))/i,
      /(?:sus\s+(?:problemas?|crisis?|luchas?|sufrimientos?)\s+(?:se\s+convierten\s+en|los\s+vivo\s+como)\s+(?:míos?|mi\s+(?:responsabilidad|fracaso|carga)))/i,
    ],
  },
  // Can never let go or stop interceding (weight: 1)
  {
    weight: 1,
    patterns: [
      /(?:no\s+(?:puedo|soy\s+capaz\s+de)\s+(?:soltar|dejar\s+ir)\s+(?:a\s+(?:nadie|ninguna\s+familia)|las\s+situaciones|los\s+problemas))/i,
      /(?:sigo\s+(?:preocupado|cargando|intercediendo|pensando)\s+(?:en|por)\s+(?:ellos|sus\s+problemas|esa\s+familia)\s+(?:aunque|incluso\s+cuando)\s+(?:no\s+(?:estoy|esté)\s+(?:con\s+ellos|orando)|ya\s+pasó))/i,
    ],
  },
];

/**
 * @param {{
 *   text:           string,
 *   overcentered:   boolean,
 *   exhaustionState: string,
 * }} params
 * @returns {{
 *   responsibilityState: string,
 *   score:               number,
 *   instruction:         string|null,
 * }}
 */
export function detectOverresponsibility({
  text            = "",
  overcentered    = false,
  exhaustionState = "estable",
}) {
  const t = text ?? "";
  let score = 0;

  for (const { weight, patterns } of OVERRESPONSIBILITY_SIGNALS) {
    if (patterns.some((p) => p.test(t))) score += weight;
  }

  // Structural overcentering elevates interior overresponsibility likelihood
  if (overcentered) score += 1;

  // Exhaustion amplifies
  if (exhaustionState === "desgaste_profundo" || exhaustionState === "saturación_crítica") score += 1;

  const responsibilityState =
    score >= 6 ? "centralización_crítica" :
    score >= 4 ? "sobreresponsabilidad"   :
    score >= 2 ? "tendencia_sobrecarga"   :
    score >= 1 ? "centrado_saludable"     :
    "distribuido";

  return {
    responsibilityState,
    score: Math.round(score * 10) / 10,
    instruction: _instruction(responsibilityState),
  };
}

function _instruction(state) {
  if (state === "centralización_crítica") {
    return "La responsabilidad espiritual está centralizada de forma crítica. Acompaña con suavidad y reduce toda presión — Dios es el pastor, no el ministro humano solo.";
  }
  if (state === "sobreresponsabilidad") {
    return "La sobre-responsabilidad está elevando la tensión pastoral. Reduce la presión implícita y recuerda que el pastor es un instrumento, no el origen.";
  }
  if (state === "tendencia_sobrecarga") {
    return "Hay tendencia a absorber más de lo que le corresponde. Responde con humanidad y sin reforzar la autoexigencia.";
  }
  return null;
}
